import { DirectionEnum } from '../types/enums';
import { CinematicStep } from '../data/cinematics/types';
import { hubService } from './HubService';
import { cinematicService } from './CinematicService';
import { animateBubbleProgress } from './hubIntro';
import { useUnlockedDungeonsStore } from '../store/unlockedDungeonsSlice';
import { useHubProgressStore } from '../store/hubProgressSlice';
import { useNpcStore } from '../store/npcSlice';
import { useCinematicStore } from '../store/cinematicSlice';

const WAKE_GETUP_MS = 1200;
const WAKE_WALK_MS = 700;
const CAMERA_MOVE_MS = 700;
const TOUR_ZOOM = 1.6;
const TOUR_ZOOM_MS = 500;
const PAN_PAN_NAME = 'Rayane Bensetti, le lapin des rêves';
const PAN_PAN_PORTRAIT: string | undefined = 'face-pan-pan';
const INTRO_LINE = 'Tient, on dirait que tu rêves encore à moitié.';

// Pan Pan's line for each NPC during the reveal tour, keyed by npcId.
const NPC_TOUR_LINES: Record<string, string> = {
  'chest-keeper':
    'Chez cette brave chose, tu peux stocker tes objets pour ne pas les perdre.',
  shop: 'Ici, tu peux acheter et vendre des choses.',
  commode:
    'Oh, juste à moitié dans le rêve ! Tu peux prendre soin de tes trésors et babioles ici.',
  loadout: 'Tu peux choisir ce que tu veux mettre sur ta table de chevet.',
  bestiary: 'Ta collection de cartes faites maisons de ce dont tu rêves.',
};

const D1_UNLOCK_NPCS = ['chest-keeper', 'loadout', 'shop'];
const D2_UNLOCK_NPCS = ['bestiary', 'commode'];

async function playWakeUp(): Promise<void> {
  const bed = hubService.bed;
  hubService.playerX = bed.x;
  hubService.playerY = bed.y;
  hubService.facing = DirectionEnum.RIGHT;
  hubService.wakingUp = true;
  const spawn = hubService.getSpawnPoint('default');
  await cinematicService.play([
    { type: 'wait', ms: WAKE_GETUP_MS },
    {
      type: 'move-actor',
      actor: 'player',
      to: { x: spawn.x, y: spawn.y },
      durationMs: WAKE_WALK_MS,
    },
  ]);
  hubService.wakingUp = false;
  hubService.facing = spawn.facing;
}

async function playCloud(): Promise<void> {
  hubService.bubbleProgress = 0;
  const bubble = hubService.currentDreamBubble;
  await animateBubbleProgress(bubble?.growDurationMs ?? 1100);
}

async function playNpcTour(npcIds: string[]): Promise<void> {
  const steps: CinematicStep[] = [];
  let zoomedIn = false;
  for (const id of npcIds) {
    const placement = hubService.npcs.find((n) => n.npcId === id);
    if (!placement) {
      continue;
    }
    const move: CinematicStep = {
      type: 'camera-move',
      to: { x: placement.x, y: placement.y },
      durationMs: CAMERA_MOVE_MS,
    };
    if (!zoomedIn) {
      zoomedIn = true;
      steps.push({
        type: 'parallel',
        steps: [
          { type: 'camera-zoom', zoom: TOUR_ZOOM, durationMs: TOUR_ZOOM_MS },
          move,
        ],
      });
    } else {
      steps.push(move);
    }
    const line = NPC_TOUR_LINES[id];
    if (line) {
      steps.push({
        type: 'dialogue',
        speaker: PAN_PAN_NAME,
        portraitId: PAN_PAN_PORTRAIT,
        text: line,
      });
    }
  }
  steps.push({ type: 'camera-move', to: 'player', durationMs: CAMERA_MOVE_MS });
  steps.push({ type: 'camera-zoom', zoom: 1, durationMs: TOUR_ZOOM_MS });
  steps.push({ type: 'camera-follow', actor: 'player' });
  await cinematicService.play(steps);
}

/**
 * Run-return arrival orchestration: wake-up always, then a one-shot reveal
 * (D1 cloud + NPC tour, or D2 NPC tour). NPC unlocks happen here (the NPCs are
 * invisible interaction points, so unlocking up-front shows nothing premature).
 * Called from HubScene once the map is loaded and the cinematic adapter is
 * registered.
 */
export async function runHubArrival(): Promise<void> {
  const cinematic = useCinematicStore.getState();
  // Hold the cinematic freeze across the whole arrival: each `play()` clears
  // `active` at its end, so re-assert between segments to keep input frozen.
  cinematic.setActive(true);
  try {
    await playWakeUp();
    cinematic.setActive(true);

    const dungeons = useUnlockedDungeonsStore.getState();
    const progress = useHubProgressStore.getState();
    const npcStore = useNpcStore.getState();

    if (dungeons.isCompleted('clear-forest') && !progress.d1RevealPlayed) {
      D1_UNLOCK_NPCS.forEach((id) => npcStore.unlock(id));
      await cinematicService.play([
        {
          type: 'dialogue',
          speaker: PAN_PAN_NAME,
          portraitId: PAN_PAN_PORTRAIT,
          text: INTRO_LINE,
        },
      ]);
      cinematic.setActive(true);
      await playCloud();
      await playNpcTour(D1_UNLOCK_NPCS);
      progress.setD1RevealPlayed();
    } else if (dungeons.isCompleted('damp-caves') && !progress.d2RevealPlayed) {
      D2_UNLOCK_NPCS.forEach((id) => npcStore.unlock(id));
      await playNpcTour(D2_UNLOCK_NPCS);
      progress.setD2RevealPlayed();
    }
  } finally {
    cinematic.setActive(false);
  }
}
